import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Mail, Send, Youtube, Instagram, MessageCircle,
  MapPin, Clock, CheckCircle
} from 'lucide-react'
import AnimatedSection from '../components/AnimatedSection'
import SectionHeading from '../components/SectionHeading'

const socialLinks = [
  {
    name: 'YouTube',
    handle: '@fivetrader',
    url: 'https://www.youtube.com/@fivetrader',
    icon: Youtube,
    color: 'bg-red-500/10 text-red-500',
    desc: 'Subscribe for weekly trading videos',
  },
  {
    name: 'Instagram',
    handle: '@fivetraderz',
    url: 'https://www.instagram.com/fivetraderz/',
    icon: Instagram,
    color: 'bg-pink-500/10 text-pink-500',
    desc: 'Daily market updates & tips',
  },
  {
    name: 'Telegram',
    handle: 'FiveTrader Community',
    url: 'https://t.me/+lJRKb2LzvTwxMTZl',
    icon: MessageCircle,
    color: 'bg-sky-500/10 text-sky-500',
    desc: 'Join our trading community',
  },
]

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' })
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitted(true)
    setTimeout(() => {
      setSubmitted(false)
      setFormData({ name: '', email: '', message: '' })
    }, 3000)
  }

  return (
    <div className="min-h-screen pt-24 md:pt-28">
      {/* Hero */}
      <section className="relative pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 via-transparent to-brand-600/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase bg-brand-500/10 text-brand-600 dark:text-brand-400 mb-4"
          >
            Get in Touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--text-primary)] mb-6 text-balance"
          >
            Let us <span className="gradient-text">Connect</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[var(--text-secondary)] max-w-2xl mx-auto text-balance"
          >
            Have questions about trading, want to collaborate, or just want to say hello?
            Reach out through any of our channels below.
          </motion.p>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-12 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left: Info */}
            <AnimatedSection>
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-4">Contact Information</h3>
                  <p className="text-[var(--text-secondary)] leading-relaxed mb-6">
                    We are always happy to hear from our community. Whether you have a question about
                    a video topic, want to suggest content, or explore collaboration opportunities.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-4 glass rounded-2xl p-5">
                    <div className="w-11 h-11 rounded-xl bg-brand-500/10 flex items-center justify-center">
                      <Mail size={20} className="text-brand-600 dark:text-brand-400" />
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase tracking-wide">Email</p>
                      <a href="mailto:hostinghubx@gmail.com" className="text-[var(--text-primary)] font-medium hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                        hostinghubx@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 glass rounded-2xl p-5">
                    <div className="w-11 h-11 rounded-xl bg-brand-500/10 flex items-center justify-center">
                      <MapPin size={20} className="text-brand-600 dark:text-brand-400" />
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase tracking-wide">Location</p>
                      <p className="text-[var(--text-primary)] font-medium">India</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 glass rounded-2xl p-5">
                    <div className="w-11 h-11 rounded-xl bg-brand-500/10 flex items-center justify-center">
                      <Clock size={20} className="text-brand-600 dark:text-brand-400" />
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase tracking-wide">Response Time</p>
                      <p className="text-[var(--text-primary)] font-medium">Within 24-48 hours</p>
                    </div>
                  </div>
                </div>

                {/* Social Cards */}
                <div className="pt-4">
                  <h4 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-4">Follow Us</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {socialLinks.map((social, i) => (
                      <a
                        key={i}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group glass rounded-2xl p-5 text-center card-hover"
                      >
                        <div className={`w-10 h-10 rounded-xl ${social.color} flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                          <social.icon size={20} />
                        </div>
                        <p className="text-sm font-semibold text-[var(--text-primary)]">{social.name}</p>
                        <p className="text-xs text-[var(--text-muted)] mt-0.5">{social.handle}</p>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Right: Form */}
            <AnimatedSection delay={0.2}>
              <div className="glass rounded-3xl p-8 md:p-10">
                <h3 className="text-xl font-bold text-[var(--text-primary)] mb-6">Send a Message</h3>

                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center py-12 text-center"
                  >
                    <div className="w-16 h-16 rounded-full bg-brand-500/10 flex items-center justify-center mb-4">
                      <CheckCircle size={32} className="text-brand-600 dark:text-brand-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Message Sent!</h4>
                    <p className="text-sm text-[var(--text-secondary)]">Thank you for reaching out. We will get back to you soon.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Your Name</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500/50 transition-all"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Email Address</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500/50 transition-all"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Message</label>
                      <textarea
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500/50 transition-all resize-none"
                        placeholder="Tell us how we can help..."
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full group inline-flex items-center justify-center gap-2 px-6 py-4 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-brand-500/25 hover:shadow-brand-500/40"
                    >
                      Send Message
                      <Send size={16} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </form>
                )}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section className="py-12 bg-[var(--bg-secondary)] border-t border-[var(--border-color)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <div className="glass rounded-2xl p-6 md:p-8 border border-brand-500/10">
              <h4 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-3">Important Notice</h4>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                <strong>Disclaimer:</strong> The information in my videos, analyses, and writings is solely for educational purposes,
                not investment advice. I cannot guarantee the accuracy of the information shared, so please consult your financial advisor
                before making any decisions. I am not SEBI registered.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  )
}
